import { useState, useEffect } from "react";
import {
  BarChart3,
  FileUp,
  Video,
  Receipt,
  Loader2,
  AlertCircle,
  Mail,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import logo from "../assets/LOGO AZUL OSCURO.png";

const FEATURES = [
  {
    icon: BarChart3,
    title: "Seguimiento en Tiempo Real",
    desc: "Visualiza tu progreso por cada fase del programa y mantente al día con tus objetivos.",
  },
  {
    icon: FileUp,
    title: "Gestión de Documentos",
    desc: "Sube tus ejercicios, descarga material y ten todo organizado en un solo lugar.",
  },
  {
    icon: Video,
    title: "Sesiones de Coaching",
    desc: "Consulta tus próximas sesiones y no te pierdas ninguna llamada importante.",
  },
  {
    icon: Receipt,
    title: "Facturación Transparente",
    desc: "Consulta el estado de tus pagos y descarga tus facturas cuando las necesites.",
  },
];

export default function Login() {
  const { signIn, signInWithMagicLink } = useAuth();

  const [mode, setMode] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [linkExpired, setLinkExpired] = useState(false);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("error=")) {
      const params = new URLSearchParams(hash.replace("#", ""));
      const errorCode = params.get("error_code");
      
      if (errorCode === "otp_expired" || hash.includes("expired")) {
        setLinkExpired(true);
        setMode("magic");
      } else {
        setError("Ha ocurrido un error. Inténtalo de nuevo.");
      }
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signIn(email, password);
    } catch {
      setError("Email o contraseña incorrectos");
      setLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signInWithMagicLink(email);
      setMagicLinkSent(true);
      setLinkExpired(false);
    } catch {
      setError("No se pudo enviar el enlace. Verifica tu email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-navy via-navy-light to-slate-800 relative overflow-hidden">
      <div className="absolute -top-1/2 -right-1/5 w-4/5 h-[200%] bg-[radial-gradient(circle,rgba(108,200,196,0.06)_0%,transparent_70%)] pointer-events-none" />

      <div className="w-full max-w-5xl mx-auto flex flex-col lg:flex-row items-center gap-12 px-6 py-8 relative z-10">
        {/* Left — branding */}
        <div className="flex-1 text-center lg:text-left max-lg:max-w-md">
          <div className="mb-8 flex flex-col items-center lg:items-start">
            <img
              src={logo}
              alt="Entrena Tu Centro"
              className="h-20 mb-4 brightness-0 invert"
            />
            <p className="text-gray-400 mt-1">
              Portal del Alumno · Programa Gestión Inteligente
            </p>
          </div>

          <div className="space-y-5 max-lg:hidden">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex gap-4 items-start">
                <div className="w-11 h-11 rounded-xl bg-brand-accent/15 flex items-center justify-center text-brand-accent shrink-0">
                  <Icon size={20} />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">{title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — form */}
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-2xl p-8 shadow-2xl shadow-black/30">
            <h2 className="text-2xl font-bold text-gray-900 text-center">
              Bienvenido/a
            </h2>
            <p className="text-gray-500 text-sm mt-1 mb-8 text-center">
              Accede a tu portal de alumno
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-sm text-red-600">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {linkExpired && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
                  <Clock size={18} />
                  Enlace caducado
                </div>
                <p className="text-sm text-amber-600">
                  El enlace de acceso ha expirado. Introduce tu email para recibir uno nuevo.
                </p>
              </div>
            )}

            {magicLinkSent ? (
              <div className="text-center py-4">
                <CheckCircle2
                  size={40}
                  className="text-emerald-500 mx-auto mb-3"
                />
                <h3 className="font-semibold text-gray-900 mb-1">
                  Enlace enviado
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Revisa tu bandeja de entrada en{" "}
                  <strong>{email}</strong> y haz clic en el enlace para acceder.
                </p>
                <button
                  onClick={() => {
                    setMagicLinkSent(false);
                    setMode("password");
                  }}
                  className="text-sm text-brand hover:text-brand-dark font-medium"
                >
                  Volver al login
                </button>
              </div>
            ) : mode === "password" ? (
              <form onSubmit={handlePasswordLogin} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    required
                    className="w-full px-4 py-3 border-[1.5px] border-gray-200 rounded-xl text-base bg-gray-50 outline-none transition-all focus:border-brand focus:bg-white focus:ring-3 focus:ring-brand/10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5">
                    Contraseña
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="w-full px-4 py-3 border-[1.5px] border-gray-200 rounded-xl text-base bg-gray-50 outline-none transition-all focus:border-brand focus:bg-white focus:ring-3 focus:ring-brand/10"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-brand hover:bg-brand-dark text-white rounded-xl text-sm font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-brand/30 disabled:opacity-70 disabled:translate-y-0 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Accediendo...
                    </>
                  ) : (
                    "Acceder al Portal"
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setMode("magic")}
                  className="w-full text-center text-sm text-gray-400 hover:text-brand transition-colors"
                >
                  Acceder con enlace por email
                </button>
              </form>
            ) : (
              <form onSubmit={handleMagicLink} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    required
                    className="w-full px-4 py-3 border-[1.5px] border-gray-200 rounded-xl text-base bg-gray-50 outline-none transition-all focus:border-brand focus:bg-white focus:ring-3 focus:ring-brand/10"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-brand hover:bg-brand-dark text-white rounded-xl text-sm font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-brand/30 disabled:opacity-70 disabled:translate-y-0 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Mail size={16} />
                      Enviar enlace de acceso
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setMode("password")}
                  className="w-full text-center text-sm text-gray-400 hover:text-brand transition-colors"
                >
                  Acceder con contraseña
                </button>
              </form>
            )}

            <p className="text-center text-xs text-gray-400 mt-6">
              ¿Problemas para acceder? Contacta con tu consultor
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
